/**
 * shared/position.ts — where somebody is in a book.
 *
 * Four projections of position exist and are never confused (§2.1):
 *
 *   sid   "c003.b0042.s2"   position in an edition   invalidated by NOTHING (editions are immutable)
 *   ord   dense 0-based int position in an edition   invalidated by nothing
 *   ms    float on the book's linear narration axis  invalidated by narrating a page
 *   frac  0..1 within the sentence                   —
 *
 * LAW 3. `ms` IS ADVISORY. It is never used to reconstruct a position. A client resolves an in-page
 * offset from ITS OWN cue list — see `resolveCueMs()` in shared/narration/cue.ts. `ms` exists for the
 * scrubber, the "14 min left in chapter" label and the co-read tick, and for nothing else.
 * Subtracting two `ms` values that came from different timeline versions is how you get a
 * confidently-wrong highlight tens of seconds off by mid-book, with no error anywhere.
 */

/**
 * A position in a book. All four fields travel together, always.
 *
 * `sid` is the truth. `ord` is what you compare with. `ms` is what you render. `frac` is where inside
 * the sentence — it is what makes "resume exactly where you stopped" survive a re-narration, because
 * a fraction of a sentence means the same thing on every timeline version and a millisecond does not.
 */
export interface BookPos {
  sid: string;
  ord: number;
  ms: number;
  /** 0..1 through the sentence. NEVER coerced from absent — see LAW 9. */
  frac: number;
}

export type PlaybackMode = "listen" | "read";

/** The three rows in `positions`. `furthest` is OFFERED, never applied — see `newerPosition()`. */
export type PositionKind = "read" | "listen" | "furthest";

/**
 * The clock fields of a room's playback state, movies' shape field for field.
 *
 * Declared structurally here rather than imported from shared/protocol.ts because protocol.ts is P5
 * and this arithmetic is needed from P1 onwards (the progress footer and the scrubber both use it).
 * P5's `Playback` satisfies this interface; do not fork the formula.
 */
export interface PlaybackClock {
  playing: boolean;
  mode: PlaybackMode;
  positionSec: number;
  anchorServerTime: number;
  rate: number;
}

/**
 * The authoritative clock. Sync the CONTROLS, not the media: the server never streams into a room, it
 * relays control intents and holds this one formula.
 *
 * `mode === "read"` has NO clock — `playing` is always false, the anchor does not tick, and correction
 * is discrete (a 400 ms eased page turn), because movies' continuous nudge is physically wrong for
 * silent reading. Re-reading a paragraph is normal; yanking someone out of it is hostile.
 */
export const predictPosition = (pb: PlaybackClock, now: number): number =>
  pb.playing && pb.mode === "listen"
    ? pb.positionSec + ((now - pb.anchorServerTime) / 1000) * pb.rate
    : pb.positionSec;

/**
 * Order two positions. By `ord`, then by `frac`.
 *
 * NOT by `ms` — LAW 3. Two clients in one room can hold different timeline versions, and comparing
 * their milliseconds decides "who is ahead" from an implementation detail that a single stitch job
 * can move by 400 ms. `ord` cannot move, because editions are immutable.
 */
export function comparePos(a: BookPos, b: BookPos): number {
  if (a.ord !== b.ord) return a.ord - b.ord;
  if (a.frac !== b.frac) return a.frac - b.frac;
  return a.sid < b.sid ? -1 : a.sid > b.sid ? 1 : 0;
}

export const samePos = (a: BookPos, b: BookPos): boolean => a.sid === b.sid && a.frac === b.frac;

/** "Karti is 3 pages ahead" comes from this, never from `ms`. */
export const posDeltaOrd = (a: BookPos, b: BookPos): number => a.ord - b.ord;

/** A position as stored, with the edition it is scoped to (LAW 1) and the clock that wrote it. */
export interface StoredPosition extends BookPos {
  editionId: string;
  chapterOrd: number;
  kind: PositionKind;
  deviceId?: string | null;
  /** The CLIENT's clock at the moment of the write. Server time would order two devices by round-trip. */
  clientUpdatedAt: number;
}

/**
 * Cross-device position sync outside rooms is LAST-WRITE-WINS, not furthest-wins.
 *
 * Re-reading a chapter legitimately moves you backwards, and furthest-wins yanks you forward every
 * time you open the app. The compensating affordance is a dismissible 10 s pill — "Jump to your
 * furthest point · Chapter 14" — offered, never applied. That is what the separate `furthest` row is
 * for; it is never merged into `read` or `listen` automatically.
 */
export function newerPosition<T extends { clientUpdatedAt: number }>(a: T, b: T): T {
  return b.clientUpdatedAt > a.clientUpdatedAt ? b : a;
}

// ─── the wire (P5) ───────────────────────────────────────────────────────────
//
// A position arriving over the WebSocket is an untrusted integer soup, and it arrives at 2/s per
// member on the same event loop that is streaming audio. These two functions are the whole of the
// server's defence, and they are here rather than in the socket layer so that the client validates
// with the SAME code before it sends — a client that ships a NaN and finds out from a silent drop
// three hops later is a bug nobody can reproduce.

/**
 * ⚠️ **LAW 9. Absent is not zero.** A missing field is `null`, never a default.
 *
 * `frac ?? 0` is how a dropped field becomes a room that resumes everybody at the *top* of the
 * sentence somebody was halfway through, and `ord ?? 0` is how it becomes chapter one. Neither
 * produces an error; both produce a room that is confidently in the wrong place, which is exactly
 * the class of failure this whole coordinate system exists to make impossible.
 *
 * `ms` is required on the wire too, even though LAW 3 makes it advisory: a sender that omits it is a
 * sender that has not run the axis, and accepting a zero there would put a room's scrubber at the
 * start of the book while its highlight sat in chapter nine.
 */
export function parseBookPos(v: unknown): BookPos | null {
  if (v === null || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  if (typeof o.sid !== "string" || o.sid.length === 0 || o.sid.length > 64) return null;
  if (typeof o.ord !== "number" || !Number.isFinite(o.ord)) return null;
  if (typeof o.ms !== "number" || !Number.isFinite(o.ms)) return null;
  if (typeof o.frac !== "number" || !Number.isFinite(o.frac)) return null;
  return {
    sid: o.sid,
    // Re-clamped, not rejected: a negative ord is a client arithmetic slip, and refusing the whole
    // message would stall that member's presence rather than correct it. A NaN is refused above,
    // because there is no correct value to clamp it to.
    ord: Math.max(0, Math.trunc(o.ord)),
    ms: Math.max(0, o.ms),
    frac: Math.min(1, Math.max(0, o.frac)),
  };
}

/**
 * ⚠️ **Re-clamp every number AFTER the permission check, never instead of it.**
 *
 * movies' rule, and it is the one that reads as paranoia until you say it out loud: a member with
 * legitimate control is still an untrusted source of integers. Holding the remote means you may
 * change the rate; it does not mean you may set it to `1e308` and pin everyone's decoder.
 */
export const clampRate = (rate: unknown): number =>
  Math.min(4, Math.max(0.25, typeof rate === "number" && Number.isFinite(rate) ? rate : 1));

/**
 * Re-express a position on a new timeline version — **by `sid`, never by `ms`** (§7.3).
 *
 * `axisOf` is a lookup into the NEW axis. Sentences are what a human perceives and seconds are an
 * implementation detail: migrating by seconds after a 400 ms axis shift lands you mid-word in a
 * different sentence, with the highlight wrong and no error anywhere. When the new axis does not
 * know the sid at all the position is returned **unchanged** rather than zeroed — a stale `ms` is
 * advisory and harmless (LAW 3), and a zeroed one is a room that jumped to the start of the book.
 *
 * `sid`, `ord` and `frac` are carried across untouched. Only `ms` moves, because `ms` is the only
 * one of the four that a timeline version can invalidate (§2.1's table).
 */
export function migratePos(
  at: BookPos,
  axisOf: (sid: string) => { startMs: number; durMs: number } | null,
): BookPos {
  const point = axisOf(at.sid);
  if (point === null) return at;
  return { ...at, ms: point.startMs + at.frac * point.durMs };
}

/**
 * Is `member` far enough behind `room` to block the room's `sid` advance? §7.4's `behind`.
 *
 * `slack` is in **`ord` units — sentences**, because `ord` is the only unit the rule is stated in
 * and the only one that means the same thing on a phone and a MacBook. It is deliberately not pages:
 * a canonical page is ~1500 characters and a display page is whatever the reader's font size makes
 * it, so "one page of slack" is two different amounts of slack for the two people in the room.
 *
 * Derived, never latched. That is what lets `behind` carry no deadline without violating LAW 20.
 */
export const isBehind = (member: BookPos | null, room: BookPos, slack: number): boolean =>
  member !== null && room.ord - member.ord > Math.max(0, slack);
